import type { ChatTarget } from './types';

/**
 * Per-kind derivations for a chat thread.
 *
 * These are exhaustive switches rather than ternaries on purpose. filterColumn() picks
 * the column a message is written to and read from, so a kind that silently falls into
 * the wrong branch is a security bug, not a display bug -- with a switch, adding a fourth
 * kind fails the build here instead.
 */

/**
 * Stable string identity for a thread. Used as the IndexedDB cache key, the realtime
 * channel suffix, and the reset key for per-thread effects.
 */
export function chatKey(target: ChatTarget): string {
  switch (target.kind) {
    case 'event':
      return `event:${target.eventId}`;
    case 'direct':
      return `direct:${target.directChatId}`;
    case 'department':
      return `department:${target.departmentChatId}`;
  }
}

/** The messages_new / message_reads column this thread's rows are keyed by. */
export function filterColumn(
  target: ChatTarget
): 'event_id' | 'direct_chat_id' | 'department_chat_id' {
  switch (target.kind) {
    case 'event':
      return 'event_id';
    case 'direct':
      return 'direct_chat_id';
    case 'department':
      return 'department_chat_id';
  }
}

/** The id stored in that column. */
export function targetId(target: ChatTarget): string {
  switch (target.kind) {
    case 'event':
      return target.eventId;
    case 'direct':
      return target.directChatId;
    case 'department':
      return target.departmentChatId;
  }
}

/**
 * Rebuild a target from a kind and the id read back out of its column. The inverse of
 * filterColumn/targetId, for code that queries by column and has to get back to a key.
 */
export function targetForKind(kind: ChatTarget['kind'], id: string): ChatTarget {
  switch (kind) {
    case 'event':
      return { kind: 'event', eventId: id };
    case 'direct':
      return { kind: 'direct', directChatId: id };
    case 'department':
      return { kind: 'department', departmentChatId: id };
  }
}

/**
 * Storage prefix for attachments. Direct and department threads are namespaced under
 * `dm/` and `dept/` so the bucket policies in the chat attachment scoping migrations can
 * tell the three apart from the object path alone.
 */
export function storagePrefix(target: ChatTarget): string {
  switch (target.kind) {
    case 'event':
      return target.eventId;
    case 'direct':
      return `dm/${target.directChatId}`;
    case 'department':
      return `dept/${target.departmentChatId}`;
  }
}

/** Column/value pair to write when inserting a message into this thread. */
export function threadInsertKey(target: ChatTarget): Record<string, string> {
  return { [filterColumn(target)]: targetId(target) };
}

/**
 * Parse a chatKey() string back into a target, or null if it is not one.
 *
 * The database writes these strings too: a chat notification carries its thread as
 * `data.chat_key` and deep-links with it, so this is the client side of a contract
 * shared with notify_chat_message() in
 * supabase/migrations/20260909120100_chat_message_notifications.sql. Keep it the exact
 * inverse of chatKey() -- the round trip is covered in chatTarget.test.ts.
 */
export function chatKeyToTarget(key: string): ChatTarget | null {
  const separator = key.indexOf(':');
  if (separator <= 0) return null;

  const id = key.slice(separator + 1);
  if (!id) return null;

  switch (key.slice(0, separator)) {
    case 'event':
      return { kind: 'event', eventId: id };
    case 'direct':
      return { kind: 'direct', directChatId: id };
    case 'department':
      return { kind: 'department', departmentChatId: id };
    default:
      return null;
  }
}
